import { type OptionsBlockSpecsFragment } from '../../../../api/fragments/OptionsBlockSpecs';
import {
    type OptionsBlockInput as ApiOptionsBlockInput,
    type UploadedFileWithPreview,
    ComboType,
    BlockType,
    OptionType,
    type ComboOptionInput,
    type OptionSettingsInput,
    type BlockInput,
    OptionKind,
    type OptionInput,
} from '../../../../api/types';

export type OptionsBlockInput = Omit<ApiOptionsBlockInput, 'sectionImages' | 'optionSettings'> & {
    sectionImages?: (File | UploadedFileWithPreview)[];
    optionSettings: Omit<OptionSettingsInput, 'singleSelectOptionSettingsInput'> & {
        singleSelectOptionSettingsInput?: {
            options: (OptionInput & {
                image?: File | UploadedFileWithPreview;
            })[];
        };
    };
};

export type OptionBlockValue = {
    id?: string;
    type: BlockType;
    optionsBlockInput: OptionsBlockInput;
};

export type OptionBlockFormValues = {
    optionsBlocks: OptionBlockValue[];
};

export const FORM_NAME = 'variantConfiguratorOptionsBlocks';

export const getDefaultOptionTypeInput = () => ({
    label: {
        defaultValue: '',
        overrides: [],
    },
    type: OptionType.Description,
    valueText: {
        defaultValue: '',
        overrides: [],
    },
});

export const getDefaultOptionComboTypeInput = (): ComboOptionInput => ({
    label: {
        defaultValue: '',
        overrides: [],
    },
    type: ComboType.TextField,
    descriptions: [{ defaultValue: '', overrides: [] }],
    placeholder: '',
    charLimit: 0,
});

export const getDefaultOptionSettings = (): OptionSettingsInput => ({
    type: OptionKind.SingleSelect,
    singleSelectOptionSettingsInput: {
        options: [getDefaultOptionTypeInput()],
    },
    multiSelectOptionSettingsInput: {
        options: [getDefaultOptionTypeInput()],
    },
    comboOptionSettingsInput: {
        price: 0,
        options: [getDefaultOptionComboTypeInput()],
    },
});

export const getOptionBlockInput = (): Pick<BlockInput, 'type'> & {
    optionsBlockInput: OptionsBlockInput;
} => ({
    type: BlockType.Options,
    optionsBlockInput: {
        financing: false,
        isActive: true,
        optionSettings: getDefaultOptionSettings(),
        sectionTitle: {
            defaultValue: '',
            overrides: [],
        },
        sectionDescription: {
            defaultValue: '',
            overrides: [],
        },
        sectionImages: [],
        order: 0,
    },
});

export const mapOptionSettings = (setting: OptionsBlockSpecsFragment['optionSettings']) => {
    switch (setting.__typename) {
        case 'SingleSelectOptionSettings':
            return {
                type: OptionKind.SingleSelect,
                singleSelectOptionSettingsInput: {
                    options: setting.options.map(({ image, ...option }) => ({
                        ...option,
                        image: image as UploadedFileWithPreview,
                    })),
                },
            };

        case 'MultiSelectOptionSettings':
            return {
                type: OptionKind.MultiSelect,
                multiSelectOptionSettingsInput: {
                    options: setting.options,
                },
            };

        case 'ComboOptionSettings':
            return {
                type: OptionKind.Combo,
                comboOptionSettingsInput: {
                    options: setting.options.map(({ comboType, ...option }) => ({
                        ...option,
                        type: comboType,
                    })),
                    price: setting.price,
                },
            };

        case 'DropdownOptionSettings':
            return {
                type: OptionKind.Dropdown,
                dropdownOptionSettingsInput: {
                    options: setting.options,
                },
            };

        default:
            throw new Error('Unknown option type');
    }
};
